import { memo, useCallback } from 'react'
import { Avatar, Box, Grid, Link, Stack, Typography, useTheme } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Security } from '../../proto/generated/security'
import { LOGOS_BASE_URL } from '../../assets/constants'
import { getColorForSymbol } from '../../util/general'
import { getSecurityKey } from '../../util/security'
import { makeStyles } from 'tss-react/mui'
import ROUTES from '../../assets/routes'
import { generatePath } from 'react-router-dom'

const useStyles = makeStyles()((theme) => ({
    container: {
        padding: '8px 16px',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    logoBox: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center'
    },
    avatar: {
        width: 24,
        height: 24,
        fontSize: 18,
        color: 'white'
    },
    link: {
        cursor: 'pointer',
        textDecoration: 'none',
        color: 'inherit',
        '&:hover': {
            color: theme.palette.info.dark
        }
    },
    openIcon: {
        fontSize: 14,
        marginLeft: theme.spacing(1),
        color: theme.palette.success.dark,
        '&:hover': {
            color: 'inherit'
        }
    },
    secondaryTextColor: {
        color: theme.palette.text.secondary
    }
}))

const SecurityOverviewHeader = ({ currentVisibleOverviewSecurity }: { currentVisibleOverviewSecurity: Security }) => {
    const { classes } = useStyles()
    const theme = useTheme()

    const handleOverviewSymbolClick = useCallback(() => {
        handleOverviewSecurityClick(currentVisibleOverviewSecurity)
    }, [currentVisibleOverviewSecurity])

    return currentVisibleOverviewSecurity ? (
        <Grid container className={classes.container}>
            <Box className={classes.logoBox}>
                {currentVisibleOverviewSecurity?.logoUrl ? (
                    <Avatar alt="Icon" src={`${LOGOS_BASE_URL}${currentVisibleOverviewSecurity.logoUrl}.svg`} className={classes.avatar} />
                ) : (
                    <Avatar
                        alt="Icon"
                        className={classes.avatar}
                        style={{
                            backgroundColor: getColorForSymbol(currentVisibleOverviewSecurity.symbol || currentVisibleOverviewSecurity.code, theme.palette.background.default)
                        }}
                    >
                        {(currentVisibleOverviewSecurity.symbol || currentVisibleOverviewSecurity.code)
                            .split(' ')
                            .map((word) => word[0]?.toUpperCase())
                            .slice(0, 2)
                            .join('')}
                    </Avatar>
                )}
                <Stack direction="row" gap={1} pl={1} alignItems="baseline">
                    <Link
                        data-event={'Symbol Chart Clicked'}
                        data-event-symbol-name={currentVisibleOverviewSecurity.symbol}
                        data-event-source={'overview'}
                        className={classes.link}
                        onClick={handleOverviewSymbolClick}
                    >
                        <Typography variant="body2" component="span">
                            {currentVisibleOverviewSecurity.symbol}
                        </Typography>
                        <OpenInNewIcon className={classes.openIcon} />
                    </Link>
                    <Typography className={classes.secondaryTextColor}>{' \u2022 '}</Typography>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                        <Typography variant="body2" className={classes.secondaryTextColor}>
                            {currentVisibleOverviewSecurity?.displayName} {currentVisibleOverviewSecurity?.instrument === 'IDX' && ' Index'}
                        </Typography>
                    </Stack>
                </Stack>
            </Box>
        </Grid>
    ) : null
}

export default memo(SecurityOverviewHeader)

export const handleOverviewSecurityClick = (security: Security | string | undefined) => {
    if (!security) {
        return null
    }
    const origin = window.location.origin
    return window.open(origin + `${generatePath(ROUTES.symbol, { symbol: typeof security === 'string' ? security : getSecurityKey(security.exchange, security.code) })}`, '_blank')
}